import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Collapse,
  Divider,
  MenuItem,
} from '@mui/material';
import { FormField } from '../../../components/ui';
import { LICENSE_LIMIT_CUSTOM_VALUE } from '../utils/licenseConstants';
import {
  getLimitSelectValue,
  isCustomLimit,
  resolveLimitDisplayLabel,
} from '../utils/licenseLimitUtils';

function parseLimitDraft(raw) {
  if (raw === '') return null;
  const parsed = Number(raw);
  if (Number.isNaN(parsed) || !Number.isInteger(parsed)) return null;
  return parsed;
}

export default function LicensePresetSelect({
  label,
  value,
  onChange,
  error,
  presetOptions = [],
  helperText,
  customHelperText = 'Enter a custom limit (minimum 1)',
  placeholder = 'Select limit...',
  required = false,
  formatCustomLabel,
  customInputLabel,
  hint,
  disabled = false,
}) {
  const customInputRef = useRef(null);

  const presetValues = useMemo(
    () => presetOptions.map((option) => option.value),
    [presetOptions]
  );

  const hasCustomValue = isCustomLimit(value, presetValues);
  const [customMode, setCustomMode] = useState(hasCustomValue);
  const [customDraft, setCustomDraft] = useState(() =>
    hasCustomValue ? String(value) : ''
  );

  useEffect(() => {
    if (hasCustomValue) {
      setCustomMode(true);
      setCustomDraft(String(value));
    }
  }, [hasCustomValue, value]);

  const selectValue = customMode
    ? LICENSE_LIMIT_CUSTOM_VALUE
    : getLimitSelectValue(value, presetValues);

  const showCustomInput = customMode;

  useEffect(() => {
    if (!showCustomInput) return undefined;

    const timer = window.setTimeout(() => {
      customInputRef.current?.focus();
    }, 0);

    return () => window.clearTimeout(timer);
  }, [showCustomInput]);

  const displayCustomLabel = useCallback(
    (limitValue) => resolveLimitDisplayLabel(limitValue, presetOptions, formatCustomLabel),
    [formatCustomLabel, presetOptions]
  );

  const handleSelectChange = useCallback(
    (event) => {
      const nextValue = event.target.value;

      if (nextValue === LICENSE_LIMIT_CUSTOM_VALUE) {
        setCustomMode(true);
        setCustomDraft(isCustomLimit(value, presetValues) ? String(value) : '');
        if (!isCustomLimit(value, presetValues)) {
          onChange(null);
        }
        return;
      }

      setCustomMode(false);
      setCustomDraft('');
      onChange(nextValue);
    },
    [onChange, presetValues, value]
  );

  const handleCustomInputChange = useCallback(
    (event) => {
      const raw = event.target.value;

      if (raw !== '' && !/^\d+$/.test(raw)) {
        return;
      }

      setCustomDraft(raw);
      onChange(parseLimitDraft(raw));
    },
    [onChange]
  );

  const renderSelectValue = useCallback(
    (selected) => {
      if (selected === '' || selected === null || selected === undefined) {
        return placeholder;
      }

      if (selected === LICENSE_LIMIT_CUSTOM_VALUE) {
        if (customDraft !== '') {
          const parsed = parseLimitDraft(customDraft);
          if (parsed !== null) {
            return formatCustomLabel
              ? formatCustomLabel(parsed)
              : String(parsed);
          }
          return customDraft;
        }
        return 'Custom value...';
      }

      return displayCustomLabel(selected);
    },
    [customDraft, displayCustomLabel, formatCustomLabel, placeholder]
  );

  return (
    <Box>
      <FormField
        select
        label={label}
        hint={hint}
        required={required}
        value={selectValue}
        onChange={handleSelectChange}
        error={Boolean(error) && !showCustomInput}
        helperText={!showCustomInput ? (error || helperText) : helperText}
        disabled={disabled}
        sx={{
          mb: showCustomInput ? 0.5 : 0,
          '& .MuiSelect-select': {
            color: selectValue === '' ? 'text.secondary' : 'text.primary',
          },
        }}
        SelectProps={{
          displayEmpty: true,
          renderValue: renderSelectValue,
          MenuProps: {
            disableAutoFocusItem: true,
            autoFocus: false,
          },
        }}
      >
        <MenuItem value="" disabled>
          {placeholder}
        </MenuItem>
        {presetOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
        <Divider sx={{ my: 0.5 }} />
        <MenuItem value={LICENSE_LIMIT_CUSTOM_VALUE}>Custom value...</MenuItem>
      </FormField>

      <Collapse in={showCustomInput} unmountOnExit>
        <FormField
          type="text"
          inputMode="numeric"
          inputRef={customInputRef}
          label={customInputLabel || 'Custom limit'}
          placeholder="e.g. 15"
          value={customDraft}
          onChange={handleCustomInputChange}
          error={Boolean(error)}
          helperText={error || customHelperText}
          disabled={disabled}
          sx={{ mt: 0.5 }}
          inputProps={{
            inputMode: 'numeric',
            pattern: '[0-9]*',
            autoComplete: 'off',
          }}
        />
      </Collapse>
    </Box>
  );
}
